import { VIDEO_SIZES } from "@rakazo/contracts";

export const SLASH_ACTIONS = [
  { id: "chat-settings" as const, label: "Chat Settings" },
  { id: "settings-general" as const, label: "Settings: General" },
  { id: "settings-usage" as const, label: "Settings: Usage" },
] as const;

export type SlashActionId = (typeof SLASH_ACTIONS)[number]["id"];

/**
 * Choices a built-in media skill offers once picked in the composer. Each is sent as a
 * `Key: value` line after the message, which the skill's recipe reads; the first value is the
 * default, and "auto" or "none" sends nothing.
 */
export const SKILL_COMMAND_OPTIONS = {
  Pictures: [
    { key: "Shape", values: ["square", "landscape", "portrait"] },
    { key: "Reference", values: ["none", "screen", "chat image"] },
  ],
  Videos: [
    { key: "Size", values: ["auto", ...VIDEO_SIZES] },
    { key: "Duration", values: ["auto", "5s", "10s", "15s"] },
    { key: "Reference", values: ["none", "chat image"] },
  ],
} as const satisfies Record<string, ReadonlyArray<{ key: string; values: readonly string[] }>>;

export type SkillCommandOption = { key: string; values: readonly string[] };
export type SkillCommandChoices = Record<string, string>;

/** The composer choices for a skill by name, or none. */
export function skillCommandOptions(name: string): readonly SkillCommandOption[] {
  const needle = name.trim().toLowerCase();
  const match = Object.entries(SKILL_COMMAND_OPTIONS).find(
    ([skill]) => skill.toLowerCase() === needle,
  );
  return match ? match[1] : [];
}

/** Each option at its default. */
export function defaultSkillCommandChoices(name: string): SkillCommandChoices {
  return Object.fromEntries(
    skillCommandOptions(name).map((option) => [option.key, option.values[0] ?? ""]),
  );
}

function skillCommandLines(name: string, choices: SkillCommandChoices | undefined): string[] {
  if (!choices) return [];
  return skillCommandOptions(name).flatMap((option) => {
    const value = choices[option.key];
    if (!value || value === "auto" || value === "none" || !option.values.includes(value)) return [];
    return [`${option.key}: ${value}`];
  });
}

/**
 * Keep inline mentions in the draft; only a selected skill adds a prefix, and its composer
 * choices follow the message so mention offsets stay put.
 */
export function serializeComposerPrompt(
  draft: string,
  skill: { name: string } | null,
  choices?: SkillCommandChoices,
): string {
  const body = draft.replace(/^\s+/, "");
  if (!skill) return body.trimEnd();
  const lines = skillCommandLines(skill.name, choices);
  const text = body.trim().length > 0 ? `/${skill.name}\n${body}` : `/${skill.name}`;
  if (!lines.length) return text;
  return `${text.trimEnd()}${body.trim().length > 0 ? "\n\n" : "\n"}${lines.join("\n")}`;
}

export function truncateSlashDescription(value: string, max = 72): string {
  const text = value.replace(/\s+/g, " ").trim();
  const codePoints = Array.from(text);
  if (codePoints.length <= max) return text;
  return `${codePoints
    .slice(0, max - 1)
    .join("")
    .trimEnd()}…`;
}
